import React from 'react'

import { useHistory } from 'react-router-dom'

import { Formik } from 'formik'

import { ZariContext } from '_zari/context'
import { Col, Row, Form, Divider, Button } from '_zari/core'

import { CodigosTransaccionesConstant } from 'constants/index'

import * as RequerimientoService from 'services/requerimiento.service'

import { ValidationSchema } from './validation'

import * as FormGroups from './formGroups'
import * as Sections from './sections'
import PrevisualizarPdfModal from '../../lista/modals/previsualizarpdf'

const MainForm = ({ editarData }) => {
  const __zariContext = React.useContext(ZariContext)

  const history = useHistory()

  const detallesRef = React.useRef({})

  const [mostrarPdfModal, setMostrarPdfModal] = React.useState(false)
  const [pdfBlobUrl, setPdfBlobUrl] = React.useState('')
  const hashGuardadoRef = React.useRef(null)

  const handleClosePdfModal = React.useCallback(() => {
    setMostrarPdfModal(false)
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl)
      setPdfBlobUrl('')
    }
    history.push(`/${CodigosTransaccionesConstant.REQUERIMIENTOS_LISTA}`)
  }, [pdfBlobUrl, history])

  const formInitialValues = React.useMemo(() => {
    return {
      _idArea: editarData._idArea ?? '',
      fechaEmision:
        editarData.fechaEmision ??
        (() => {
          const d = new Date()
          return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
        })(),
      fechaNecesidad: editarData.fechaNecesidad ?? '',
      glosa: editarData.glosa ?? '',
      _idOficinaSolicitante: editarData._idOficinaSolicitante ?? '',
      observacionesGenerales: editarData.observacionesGenerales ?? '',
      prioridad: editarData.prioridad ?? 'NORMAL'
    }
  }, [editarData])

  const onSubmit = React.useCallback(
    (data, { setSubmitting }) => {
      __zariContext.loader.open('Guardando requerimiento')

      const detalles = detallesRef.current.getData()
      const detallesNormalizados = detalles.map(x => {
        return {
          unidadMedida: x.unidadMedida,
          _idProducto: x._idProducto,
          _idProductoPresentacion: x._idProductoPresentacion,
          fechaRequerimiento: x.fechaRequerimiento,
          cantidad: parseFloat(x.cantidad),
          observacion: x.observacion || ''
        }
      })
      const parseDDMMYYYY = str => {
        if (!str) return null
        const [d, m, y] = str.split('/')
        if (!d || !m || !y) return null
        return new Date(parseInt(y), parseInt(m) - 1, parseInt(d))
      }
      const fechaEmisionParsed = parseDDMMYYYY(data.fechaEmision)
      const detalleInvalido = detallesNormalizados.find(x => {
        const fechaReq = parseDDMMYYYY(x.fechaRequerimiento)
        return fechaReq && fechaEmisionParsed && fechaReq < fechaEmisionParsed
      })

      if (detalleInvalido) {
        __zariContext.snack(
          'error',
          'Las fechas de necesidad de los detalles deben ser mayores o iguales a la fecha de emisión.'
        )
        setSubmitting(false)
        __zariContext.loader.close()
        return
      }

      const funcionEjecutar = editarData._id
        ? () => RequerimientoService.actualizarxId(editarData._id, { ...data, detalles: detallesNormalizados })
        : () => RequerimientoService.nuevo({ ...data, detalles: detallesNormalizados })

      funcionEjecutar()
        .then(({ data: data2 }) => {
          if (!editarData._id) {
            RequerimientoService.descargarPdfA4xId(data2.hash)
              .then(({ data }) => {
                __zariContext.toast('success', 'Requerimiento guardado.')
                const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
                setPdfBlobUrl(url)
                setMostrarPdfModal(true)
              })
              .catch(() => {
                __zariContext.snack(
                  'warning',
                  'El requerimiento se guardó pero no se pudo generar la previsualización.'
                )
                history.push(`/${CodigosTransaccionesConstant.REQUERIMIENTOS_LISTA}`)
              })
          } else {
            __zariContext.toast('success', 'Requerimiento guardado.')
            history.push(`/${CodigosTransaccionesConstant.REQUERIMIENTOS_LISTA}`)
          }
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          setSubmitting(false)
        })
        .finally(() => {
          __zariContext.loader.close()
        })
    },
    [__zariContext, editarData, history]
  )

  return (
    <>
      <Row>
        <Col md={12}>
          <Formik initialValues={formInitialValues} onSubmit={onSubmit} validationSchema={ValidationSchema}>
            {({ errors, touched, isSubmitting, handleSubmit, values }) => (
              <Form>
                <Row>
                  <Col md={6}>
                    <FormGroups.FechaEmisionFormGroup
                      iError={errors.fechaEmision}
                      iTouched={touched.fechaEmision}
                      fIsSubmitting={isSubmitting}
                    />
                    <FormGroups.GlosaFormGroup
                      iError={errors.glosa}
                      iTouched={touched.glosa}
                      fIsSubmitting={isSubmitting}
                    />
                    {editarData._id && <FormGroups.CorrelativoFormGroup iValue={editarData.correlativo} />}
                  </Col>
                  <Col md={6}>
                    <FormGroups.OficinaFormGroup
                      iError={errors._idOficinaSolicitante}
                      iTouched={touched._idOficinaSolicitante}
                      fIsSubmitting={isSubmitting}
                    />
                    <FormGroups.FechaNecesidadFormGroup
                      iError={errors.fechaNecesidad}
                      iTouched={touched.fechaNecesidad}
                      fIsSubmitting={isSubmitting}
                    />
                    <FormGroups.PrioridadFormGroup
                      iError={errors.prioridad}
                      iTouched={touched.prioridad}
                      fIsSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Divider />
                <Sections.DetallesSection
                  detallesRef={detallesRef}
                  editarData={editarData}
                  fechaNecesidad={values.fechaNecesidad}
                />
                <Divider />
                <Row>
                  <Col md={12} className='text-left'>
                    <FormGroups.ObservacionesGeneralesFormGroup
                      iError={errors.observacionesGenerales}
                      iTouched={touched.observacionesGenerales}
                      fIsSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} className='text-right'>
                    <Button onClick={handleSubmit} disabled={isSubmitting}>
                      Guardar
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
      {mostrarPdfModal && (
        <PrevisualizarPdfModal show={mostrarPdfModal} handleClose={handleClosePdfModal} pdfBlobUrl={pdfBlobUrl} />
      )}
    </>
  )
}

export default React.memo(MainForm)
